import { useEffect, useRef, useState } from "react";
import { fieldErrorsOf, isStaleWrite, staleWriteCurrent } from "@repo/api-client";
import type {
  StaffBookingResponse,
  StaffBookingSummaryResponse,
  StaffEditBookingRequest,
} from "@repo/api-client";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useVenue } from "../shell/VenueContext";
import { BookingConflictModal } from "./BookingConflictModal";
import { Overlay } from "./ManualEntryDialog";
import { bookingClock, epochToWallInputs, wallClockToEpochSec } from "./format";
import { useEditBooking } from "./mutations";
import { useServices } from "./queries";
import {
  bookingRejectionOf,
  hasAlternatives,
  isOverridable,
  rejectionMessageKey,
  type BookingRejection,
} from "./rejections";
import { banner, fieldLabel, primaryButton, secondaryButton, textInput } from "./styles";

/**
 * Edit / move / reschedule (R2, BK-4/BK-7). A field-mask PATCH re-runs the availability check server-
 * side; a `SlotUnavailable(alternatives)` blocks the save and offers clickable alternatives, a lead-
 * time/horizon block shows the diner-equivalent message + a MANAGER override, and a 409 stale-write
 * opens the canonical conflict modal (reload their record OR re-confirm rebased on it — never
 * last-write-wins, §D.3).
 */
export function EditBookingDialog({
  booking,
  onClose,
  onSaved,
  canOverride,
}: {
  booking: StaffBookingSummaryResponse;
  onClose: () => void;
  onSaved: (b: StaffBookingResponse) => void;
  canOverride: boolean;
}) {
  const { venueId, venue } = useVenue();
  const { t, i18n } = useTranslation();
  const services = useServices(venueId);
  const edit = useEditBooking(venueId, booking.bookingId ?? "");
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => headingRef.current?.focus(), []);

  const initial = epochToWallInputs(booking.startSec ?? 0, venue.timeZone);
  const [serviceId, setServiceId] = useState(booking.serviceId ?? "");
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [partySize, setPartySize] = useState(booking.partySize ?? 2);
  const [zonePreference, setZonePreference] = useState(booking.zonePreference ?? "");
  const [specialRequests, setSpecialRequests] = useState(booking.specialRequests ?? "");
  const [allergyNote, setAllergyNote] = useState(booking.allergyNote ?? "");
  const [version, setVersion] = useState(booking.version ?? 0);

  const [rejection, setRejection] = useState<BookingRejection | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [conflict, setConflict] = useState<StaffBookingResponse | null>(null);
  const [overrideMode, setOverrideMode] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");

  function buildBody(v: number): StaffEditBookingRequest {
    return {
      version: v,
      newServiceId: serviceId || null,
      newStartSec: wallClockToEpochSec(date, time, venue.timeZone),
      newPartySize: partySize,
      newZonePreference: zonePreference || null,
      newSpecialRequests: specialRequests || null,
      newAllergyNote: allergyNote || null,
      overrideReason: overrideMode && overrideReason ? overrideReason : null,
    };
  }

  function save(v: number) {
    setRejection(null);
    setFieldErrors({});
    setErrorMsg(null);
    edit.mutate(buildBody(v), {
      onSuccess: (b) => onSaved(b),
      onError: (err) => {
        if (isStaleWrite(err)) {
          const current = staleWriteCurrent<StaffBookingResponse>(err);
          if (current) setConflict(current);
          else setErrorMsg(t("bookings.error.saveFailed"));
          return;
        }
        const rej = bookingRejectionOf(err);
        if (rej) {
          setRejection(rej);
          if (isOverridable(rej) && canOverride) setOverrideMode(true);
          return;
        }
        const fe = fieldErrorsOf(err);
        if (Object.keys(fe).length > 0) {
          setFieldErrors(fe);
          return;
        }
        setErrorMsg(t("bookings.error.saveFailed"));
      },
    });
  }

  const attempted = { startSec: wallClockToEpochSec(date, time, venue.timeZone), partySize };

  return (
    <>
      <Overlay onClose={onClose} label={t("bookings.edit.title")}>
        <h2
          ref={headingRef}
          tabIndex={-1}
          className={css({ fontSize: "lg", fontWeight: "bold", outline: "none" })}
        >
          {t("bookings.edit.title")}
        </h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save(version);
          }}
          className={css({ display: "flex", flexDirection: "column", gap: "3" })}
        >
          {errorMsg ? (
            <p role="alert" className={banner("error")}>
              {errorMsg}
            </p>
          ) : null}
          {rejection ? (
            <div role="alert" className={banner("error")}>
              <span>{t(rejectionMessageKey(rejection))}</span>
              {hasAlternatives(rejection) ? (
                <div className={css({ display: "flex", gap: "2", flexWrap: "wrap" })}>
                  {rejection.alternatives.map((a) => (
                    <button
                      key={a.startSec}
                      type="button"
                      className={secondaryButton}
                      onClick={() => {
                        const w = epochToWallInputs(a.startSec, venue.timeZone);
                        setDate(w.date);
                        setTime(w.time);
                        setRejection(null);
                      }}
                    >
                      {bookingClock(a.startSec, venue.timeZone, i18n.language)}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          <label className={fieldLabel}>
            {t("bookings.field.service")}
            <select
              className={textInput}
              value={serviceId}
              onChange={(e) => setServiceId(e.target.value)}
            >
              {(services.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nameFr}
                </option>
              ))}
            </select>
          </label>
          <div className={grid3}>
            <label className={fieldLabel}>
              {t("bookings.field.date")}
              <input
                type="date"
                className={textInput}
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </label>
            <label className={fieldLabel}>
              {t("bookings.field.time")}
              <input
                type="time"
                className={textInput}
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </label>
            <label className={fieldLabel}>
              {t("bookings.field.party")}
              <input
                type="number"
                min={1}
                max={500}
                className={textInput}
                value={partySize}
                onChange={(e) => setPartySize(Number(e.target.value))}
              />
              {fieldErrors.newPartySize ? (
                <span role="alert" className={css({ fontSize: "xs", color: "booking.noShow.fg" })}>
                  {fieldErrors.newPartySize}
                </span>
              ) : null}
            </label>
          </div>
          <label className={fieldLabel}>
            {t("bookings.field.zonePreference")}
            <input
              className={textInput}
              value={zonePreference}
              onChange={(e) => setZonePreference(e.target.value)}
            />
          </label>
          <label className={fieldLabel}>
            {t("bookings.field.specialRequests")}
            <input
              className={textInput}
              value={specialRequests}
              onChange={(e) => setSpecialRequests(e.target.value)}
            />
          </label>
          <label className={fieldLabel}>
            {t("bookings.field.allergyNote")}
            <input
              className={textInput}
              value={allergyNote}
              onChange={(e) => setAllergyNote(e.target.value)}
            />
          </label>

          {overrideMode ? (
            <div className={banner("warn")}>
              <span className={css({ fontWeight: "medium" })}>{t("bookings.override.title")}</span>
              <label className={fieldLabel}>
                {t("bookings.override.reason")}
                <input
                  className={textInput}
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                />
              </label>
            </div>
          ) : null}

          <div className={actions}>
            <button type="button" className={secondaryButton} onClick={onClose}>
              {t("bookings.action.cancel")}
            </button>
            <button type="submit" className={primaryButton} disabled={edit.isPending}>
              {edit.isPending ? t("bookings.action.saving") : t("bookings.edit.submit")}
            </button>
          </div>
        </form>
      </Overlay>

      {conflict ? (
        <BookingConflictModal
          attempted={attempted}
          current={conflict}
          onReload={() => {
            // Discard my attempt; adopt the authoritative record into the form + version.
            const w = epochToWallInputs(conflict.startSec ?? 0, venue.timeZone);
            setDate(w.date);
            setTime(w.time);
            setPartySize(conflict.partySize ?? partySize);
            setServiceId(conflict.serviceId ?? serviceId);
            setZonePreference(conflict.zonePreference ?? "");
            setSpecialRequests(conflict.specialRequests ?? "");
            setAllergyNote(conflict.allergyNote ?? "");
            setVersion(conflict.version ?? version);
            setConflict(null);
          }}
          onReconfirm={() => {
            // Re-apply MY changes rebased on their version (BK-5).
            const v = conflict.version ?? version;
            setVersion(v);
            setConflict(null);
            save(v);
          }}
        />
      ) : null}
    </>
  );
}

const actions = css({ display: "flex", gap: "2", justifyContent: "flex-end", flexWrap: "wrap" });
const grid3 = css({
  display: "grid",
  gridTemplateColumns: { base: "1fr", sm: "1fr 1fr 1fr" },
  gap: "3",
});
